import * as React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

type DialogProps = {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the user dismisses the dialog (Escape, backdrop, close button). */
  onClose: () => void;
  /** Restore focus here on close, including when responsive layouts move the trigger. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  /** Accessible label, used when there's no visible title to point at. */
  ariaLabel?: string;
  /** id of the visible title element; takes precedence over `ariaLabel`. */
  labelledById?: string;
  /** Extra classes for the panel. */
  className?: string;
  /** Hide the default top-right close (X) button. */
  hideClose?: boolean;
  /**
   * Keep the dialog mounted (just hidden + `inert`) when closed, instead of
   * unmounting it. Use for dialogs whose contents are expensive to (re)create
   * and benefit from staying warm across opens — e.g. a prerendered iframe.
   */
  keepMounted?: boolean;
};

/**
 * Reusable modal dialog. Renders into a portal on document.body and handles the
 * usual modal concerns: a click-to-dismiss backdrop, Escape to close, body
 * scroll lock while open, moving focus into the panel on open / restoring it on
 * close, and a lightweight Tab focus trap. Style the panel via `className`.
 *
 * Built from scratch (no dialog dependency in this project); animations use the
 * tailwindcss-animate utilities already configured in tailwind.config.js.
 */
export function Dialog({
  open,
  onClose,
  returnFocusRef,
  children,
  ariaLabel,
  labelledById,
  className,
  hideClose,
  keepMounted,
}: DialogProps) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  // Portals need a DOM. Keep the server and first hydration render identical.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  // Escape to close + lock body scroll while the dialog is open.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Move focus into the panel on open; restore it to the trigger on close.
  React.useEffect(() => {
    // Case: an initially open dialog must wait for its portal before taking focus.
    if (!open || !mounted) return;
    const returnFocusTarget =
      returnFocusRef?.current ?? (document.activeElement as HTMLElement | null);
    panelRef.current?.focus();
    return () => returnFocusTarget?.focus?.({ preventScroll: true });
  }, [open, mounted, returnFocusRef]);

  if (!mounted || (!open && !keepMounted)) return null;

  // Keep Tab focus cycling within the panel (lightweight trap).
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || e.key !== 'Tab') return;
    const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    // When kept mounted but closed, the whole overlay is `inert` (no focus, no
    // pointer, removed from the a11y tree) and faded out — so warm content
    // inside (e.g. an iframe) stays loaded but fully hidden and unreachable.
    <div
      inert={keepMounted && !open ? true : undefined}
      aria-hidden={keepMounted && !open ? true : undefined}
      className={cn(
        'fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6',
        // visibility:hidden prevents Calendly from finishing its first render.
        // Keep the warm iframe transparent instead, with inert + aria-hidden
        // excluding it from focus/accessibility. Disable iframe hit-testing
        // explicitly so the closed overlay cannot swallow mobile gestures.
        keepMounted && 'transition-opacity duration-200',
        keepMounted &&
          !open &&
          'opacity-0 pointer-events-none [&_iframe]:pointer-events-none'
      )}
    >
      {/* Backdrop */}
      <div
        aria-hidden
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-zinc-700/60 backdrop-blur-xl duration-200',
          keepMounted
            ? cn('transition-opacity', open ? 'opacity-100' : 'opacity-0')
            : 'animate-in fade-in'
        )}
      />
      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        {...(labelledById
          ? { 'aria-labelledby': labelledById }
          : { 'aria-label': ariaLabel })}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={cn(
          'relative w-full max-w-md rounded-2xl border border-hairline bg-white p-6 shadow-lift outline-none duration-200 sm:p-7',
          keepMounted
            ? cn('transition-all', open ? 'opacity-100' : 'scale-95 opacity-0')
            : 'animate-in fade-in zoom-in-95',
          className
        )}
      >
        {!hideClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 grid size-8 place-items-center rounded-full text-ink-400 transition-colors hover:bg-ink-50 hover:text-ink-700"
          >
            <X className="size-4" />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}
